import React from 'react';
import { motion } from 'framer-motion';
import { Filter, FileDown, X } from 'lucide-react';

const FilterGroupHead = ({ children, applied, onClear, loading, clearLabel }) => (
  <div className="filter-group-head">
    <h4>{children}</h4>
    {applied && (
      <button
        type="button"
        className="filter-group-clear"
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        disabled={loading}
        title="Clear this filter"
        aria-label={clearLabel || 'Clear this filter'}
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    )}
  </div>
);

const FiltersContainer = ({
  showFilters,
  setShowFilters,
  filters,
  setFilters,
  handleFilterChange,
  substores,
  citySubstores,
  cityHubs,
  cities,
  selectedLocations,
  setSelectedLocations,
  locationSearchTerm,
  setLocationSearchTerm,
  showLocationDropdown,
  setShowLocationDropdown,
  expandedCategories,
  toggleCategoryExpansion,
  expandedCities,
  toggleCityExpansion,
  campaignNames,
  selectedCampaignNames,
  setSelectedCampaignNames,
  campaignNameSearchTerm,
  setCampaignNameSearchTerm,
  showCampaignNameDropdown,
  setShowCampaignNameDropdown,
  filteredResults,
  applyingFilters,
  loading,
  applyFilters,
  handleDownloadWithCampaign,
  userGroupName,
  setUserGroupName,
  creatingUserGroup,
  userGroupProgressLabel,
  createUserGroup,
  clearFilters,
  clearFilterGroup,
  handleLocationToggle,
  handleSelectAllInScope,
  handleClearAllInScope,
  handleSelectAllInScopeItems,
  handleClearAllInScopeItems,
  handleSelectAllOutScope,
  handleClearAllOutScope,
  handleCampaignNameToggle,
  handleSelectAllCampaignNames,
  handleClearAllCampaignNames,
  filteredCampaignNames
}) => {
  return (
    <motion.div
      className="filters-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <div className="filters-header">
        <h2>Customer Filters</h2>
        <p>Filter and download customer data based on specific criteria</p>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="toggle-filters-btn"
          disabled={loading}
        >
          <Filter size={16} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="filters-container">
          <div className="filters-grid">
            {/* Date Range Filters */}
            <div className={`filter-group ${(filters.updatedAtFrom || filters.updatedAtTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.updatedAtFrom || filters.updatedAtTo)}
                onClear={() => clearFilterGroup('lastUpdated')}
                loading={loading}
                clearLabel="Clear Last Updated Date filter"
              >
                Last Updated Date
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.updatedAtFrom}
                  onChange={(e) => handleFilterChange('updatedAtFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.updatedAtTo}
                  onChange={(e) => handleFilterChange('updatedAtTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.createdOnFrom || filters.createdOnTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.createdOnFrom || filters.createdOnTo)}
                onClear={() => clearFilterGroup('createdOn')}
                loading={loading}
                clearLabel="Clear Created On filter"
              >
                Created On
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.createdOnFrom}
                  onChange={(e) => handleFilterChange('createdOnFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.createdOnTo}
                  onChange={(e) => handleFilterChange('createdOnTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.firstOrderFrom || filters.firstOrderTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.firstOrderFrom || filters.firstOrderTo)}
                onClear={() => clearFilterGroup('firstOrder')}
                loading={loading}
                clearLabel="Clear First Order Date filter"
              >
                First Order Date
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.firstOrderFrom}
                  onChange={(e) => handleFilterChange('firstOrderFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.firstOrderTo}
                  onChange={(e) => handleFilterChange('firstOrderTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.lastOrderFrom || filters.lastOrderTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.lastOrderFrom || filters.lastOrderTo)}
                onClear={() => clearFilterGroup('lastOrder')}
                loading={loading}
                clearLabel="Clear Last Order Date filter"
              >
                Last Order Date
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.lastOrderFrom}
                  onChange={(e) => handleFilterChange('lastOrderFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.lastOrderTo}
                  onChange={(e) => handleFilterChange('lastOrderTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.lastOrderExcludeFrom || filters.lastOrderExcludeTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.lastOrderExcludeFrom || filters.lastOrderExcludeTo)}
                onClear={() => clearFilterGroup('lastOrderExclude')}
                loading={loading}
                clearLabel="Clear Last Order Date exclude filter"
              >
                Last Order Date <span className="exclusion-badge">EXCLUDE</span>
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.lastOrderExcludeFrom}
                  onChange={(e) => handleFilterChange('lastOrderExcludeFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.lastOrderExcludeTo}
                  onChange={(e) => handleFilterChange('lastOrderExcludeTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Numeric Range Filters */}
            <div className={`filter-group ${(filters.lifetimeAOVMin || filters.lifetimeAOVMax) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.lifetimeAOVMin || filters.lifetimeAOVMax)}
                onClear={() => clearFilterGroup('lifetimeAOV')}
                loading={loading}
                clearLabel="Clear Lifetime AOV filter"
              >
                Lifetime AOV (₹)
              </FilterGroupHead>
              <div className="range-inputs">
                <input
                  type="number"
                  value={filters.lifetimeAOVMin}
                  onChange={(e) => handleFilterChange('lifetimeAOVMin', e.target.value)}
                  placeholder="Min"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="number"
                  value={filters.lifetimeAOVMax}
                  onChange={(e) => handleFilterChange('lifetimeAOVMax', e.target.value)}
                  placeholder="Max"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.lifetimeGMVMin || filters.lifetimeGMVMax) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.lifetimeGMVMin || filters.lifetimeGMVMax)}
                onClear={() => clearFilterGroup('lifetimeGMV')}
                loading={loading}
                clearLabel="Clear Lifetime GMV filter"
              >
                Lifetime GMV (₹)
              </FilterGroupHead>
              <div className="range-inputs">
                <input
                  type="number"
                  value={filters.lifetimeGMVMin}
                  onChange={(e) => handleFilterChange('lifetimeGMVMin', e.target.value)}
                  placeholder="Min"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="number"
                  value={filters.lifetimeGMVMax}
                  onChange={(e) => handleFilterChange('lifetimeGMVMax', e.target.value)}
                  placeholder="Max"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.totalOrdersMin || filters.totalOrdersMax) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.totalOrdersMin || filters.totalOrdersMax)}
                onClear={() => clearFilterGroup('totalOrders')}
                loading={loading}
                clearLabel="Clear Total Orders filter"
              >
                Total Orders
              </FilterGroupHead>
              <div className="range-inputs">
                <input
                  type="number"
                  value={filters.totalOrdersMin}
                  onChange={(e) => handleFilterChange('totalOrdersMin', e.target.value)}
                  placeholder="Min"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="number"
                  value={filters.totalOrdersMax}
                  onChange={(e) => handleFilterChange('totalOrdersMax', e.target.value)}
                  placeholder="Max"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.abandonedCartValueMin || filters.abandonedCartValueMax) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.abandonedCartValueMin || filters.abandonedCartValueMax)}
                onClear={() => clearFilterGroup('abandonedCartValue')}
                loading={loading}
                clearLabel="Clear Abandoned Cart Value filter"
              >
                Abandoned Cart Value (₹)
              </FilterGroupHead>
              <div className="range-inputs">
                <input
                  type="number"
                  value={filters.abandonedCartValueMin}
                  onChange={(e) => handleFilterChange('abandonedCartValueMin', e.target.value)}
                  placeholder="Min"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="number"
                  value={filters.abandonedCartValueMax}
                  onChange={(e) => handleFilterChange('abandonedCartValueMax', e.target.value)}
                  placeholder="Max"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group ${(filters.abandonedCartLastUpdatedFrom || filters.abandonedCartLastUpdatedTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.abandonedCartLastUpdatedFrom || filters.abandonedCartLastUpdatedTo)}
                onClear={() => clearFilterGroup('abandonedCartLastUpdated')}
                loading={loading}
                clearLabel="Clear Abandoned Cart Last Updated filter"
              >
                Abandoned Cart Last Updated
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.abandonedCartLastUpdatedFrom}
                  onChange={(e) => handleFilterChange('abandonedCartLastUpdatedFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.abandonedCartLastUpdatedTo}
                  onChange={(e) => handleFilterChange('abandonedCartLastUpdatedTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group campaign-name-filter-group ${selectedCampaignNames.length > 0 ? 'applied' : ''} ${showCampaignNameDropdown ? 'dropdown-open' : ''}`}>
              <FilterGroupHead
                applied={selectedCampaignNames.length > 0}
                onClear={() => clearFilterGroup('campaignName')}
                loading={loading}
                clearLabel="Clear Campaign Name filter"
              >
                Campaign Name ({campaignNames.length} available){' '}
                <span className="exclusion-badge">EXCLUDE</span>
              </FilterGroupHead>
              <div className={`campaign-name-multiselect ${showCampaignNameDropdown ? 'dropdown-open' : ''}`}>
                <div
                  className="campaign-name-selector"
                  onClick={() => !loading && setShowCampaignNameDropdown(!showCampaignNameDropdown)}
                  style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  <div className="campaign-name-display">
                    {selectedCampaignNames.length === 0 ? (
                      <span className="placeholder">
                        {loading ? 'Loading...' : (campaignNames.length > 0 ? 'Select campaign names...' : 'Loading campaign names...')}
                      </span>
                    ) : selectedCampaignNames.length === 1 ? (
                      <span className="selected-text">{selectedCampaignNames[0]}</span>
                    ) : (
                      <span className="selected-text">{selectedCampaignNames.length} campaigns selected</span>
                    )}
                  </div>
                  <div className="campaign-name-arrow">▼</div>
                </div>

                {showCampaignNameDropdown && !loading && (
                  <div className="campaign-name-dropdown">
                    <div className="campaign-name-search">
                      <input
                        type="text"
                        placeholder="Search campaign names..."
                        value={campaignNameSearchTerm}
                        onChange={(e) => setCampaignNameSearchTerm(e.target.value)}
                        className="campaign-name-search-input"
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                    </div>

                    <div className="campaign-name-actions">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAllCampaignNames();
                        }}
                        className="campaign-name-action-btn select-all"
                        disabled={loading}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearAllCampaignNames();
                        }}
                        className="campaign-name-action-btn clear-all"
                        disabled={loading}
                      >
                        Clear All
                      </button>
                    </div>

                    <div className="campaign-name-options">
                      {campaignNames.length === 0 ? (
                        <div className="no-campaign-names">Loading campaign names...</div>
                      ) : filteredCampaignNames.length === 0 ? (
                        <div className="no-campaign-names">No campaign names found matching "{campaignNameSearchTerm}"</div>
                      ) : (
                        filteredCampaignNames.map(campaignName => (
                          <label key={campaignName} className="campaign-name-option">
                            <input
                              type="checkbox"
                              checked={selectedCampaignNames.includes(campaignName)}
                              onChange={() => handleCampaignNameToggle(campaignName)}
                              onClick={(e) => e.stopPropagation()}
                              className="campaign-name-checkbox"
                              disabled={loading}
                            />
                            <span className="campaign-name-label">{campaignName}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={`filter-group ${(filters.campaignDateFrom || filters.campaignDateTo) ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!(filters.campaignDateFrom || filters.campaignDateTo)}
                onClear={() => clearFilterGroup('lastCampaignSent')}
                loading={loading}
                clearLabel="Clear Last Campaign Sent filter"
              >
                Last Campaign Sent <span className="exclusion-badge">EXCLUDE</span>
              </FilterGroupHead>
              <div className="date-inputs">
                <input
                  type="date"
                  value={filters.campaignDateFrom}
                  onChange={(e) => handleFilterChange('campaignDateFrom', e.target.value)}
                  placeholder="From"
                  className="filter-input"
                  disabled={loading}
                />
                <input
                  type="date"
                  value={filters.campaignDateTo}
                  onChange={(e) => handleFilterChange('campaignDateTo', e.target.value)}
                  placeholder="To"
                  className="filter-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={`filter-group location-filter-group ${(selectedLocations.inScope.delhi.length > 0 || selectedLocations.inScope.bangalore.length > 0 || selectedLocations.inScope.blank || selectedLocations.outScope.length > 0) ? 'applied' : ''} ${showLocationDropdown ? 'dropdown-open' : ''}`}>
              <FilterGroupHead
                applied={
                  selectedLocations.inScope.delhi.length > 0 ||
                  selectedLocations.inScope.bangalore.length > 0 ||
                  selectedLocations.inScope.blank ||
                  selectedLocations.outScope.length > 0
                }
                onClear={() => clearFilterGroup('location')}
                loading={loading}
                clearLabel="Clear Location filter"
              >
                Location
              </FilterGroupHead>
              <div className={`location-multiselect ${showLocationDropdown ? 'dropdown-open' : ''}`}>
                <div
                  className="location-selector"
                  onClick={() => !loading && setShowLocationDropdown(!showLocationDropdown)}
                  style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  <div className="location-display">
                    {!selectedLocations.inScope.delhi.length && !selectedLocations.inScope.bangalore.length && !selectedLocations.inScope.blank && !selectedLocations.outScope.length ? (
                      <span className="placeholder">
                        {loading ? 'Loading...' : 'Select locations...'}
                      </span>
                    ) : (
                      <span className="selected-text">
                        {[
                          ...(selectedLocations.inScope.delhi.length > 0 ? ['Delhi'] : []),
                          ...(selectedLocations.inScope.bangalore.length > 0 ? ['Bangalore'] : []),
                          ...(selectedLocations.inScope.blank ? ['Blank'] : []),
                          ...(selectedLocations.outScope.length > 0 ? ['OutScope'] : [])
                        ].join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="location-arrow">▼</div>
                </div>

                {showLocationDropdown && !loading && (
                  <div className="location-dropdown">
                    <div className="location-search">
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={locationSearchTerm}
                        onChange={(e) => setLocationSearchTerm(e.target.value)}
                        className="location-search-input"
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                    </div>

                    <div className="location-tree">
                      {/* InScope Section */}
                      <div className="location-category">
                        <div 
                          className="location-category-header"
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocations.inScope.delhi.length > 0 || selectedLocations.inScope.bangalore.length > 0 || selectedLocations.inScope.blank}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                // Select all InScope items (Delhi, Bangalore, and Blank) in one operation
                                handleSelectAllInScopeItems();
                              } else {
                                // Clear all InScope selections in one operation
                                handleClearAllInScopeItems();
                              }
                            }}
                            className="location-checkbox"
                            disabled={loading}
                          />
                          <span className="location-category-label">InScope</span>
                          <span 
                            className="location-expand-icon"
                            onClick={() => toggleCategoryExpansion('inScope')}
                          >
                            {expandedCategories.inScope ? '▼' : '▶'}
                          </span>
                        </div>
                        
                        {expandedCategories.inScope && (
                          <>
                            {/* Delhi */}
                            <div className="location-city">
                              <div 
                                className="location-city-header"
                                style={{ cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedLocations.inScope.delhi.length > 0}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                      handleSelectAllInScope('delhi');
                                    } else {
                                      handleClearAllInScope('delhi');
                                    }
                                  }}
                                  className="location-checkbox"
                                  disabled={loading}
                                />
                                <span className="location-city-label">Delhi</span>
                                <span 
                                  className="location-expand-icon"
                                  onClick={() => toggleCityExpansion('delhi')}
                                >
                                  {expandedCities.delhi ? '▼' : '▶'}
                                </span>
                              </div>
                              {expandedCities.delhi && (
                                <div className="location-substores">
                                  {(cityHubs.Delhi || [])
                                    .filter(hub => hub.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                                    .map(hub => (
                                      <label key={hub} className="location-option">
                                        <input
                                          type="checkbox"
                                          checked={selectedLocations.inScope.delhi.includes(hub)}
                                          onChange={() => handleLocationToggle('inScope', 'delhi', hub)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="location-checkbox"
                                          disabled={loading}
                                        />
                                        <span className="location-label">{hub}</span>
                                      </label>
                                    ))}
                                </div>
                              )}
                            </div>

                            {/* Bangalore */}
                            <div className="location-city">
                              <div 
                                className="location-city-header"
                                style={{ cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedLocations.inScope.bangalore.length > 0}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                      handleSelectAllInScope('bangalore');
                                    } else {
                                      handleClearAllInScope('bangalore');
                                    }
                                  }}
                                  className="location-checkbox"
                                  disabled={loading}
                                />
                                <span className="location-city-label">Bangalore</span>
                                <span 
                                  className="location-expand-icon"
                                  onClick={() => toggleCityExpansion('bangalore')}
                                >
                                  {expandedCities.bangalore ? '▼' : '▶'}
                                </span>
                              </div>
                              {expandedCities.bangalore && (
                                <div className="location-substores">
                                  {(cityHubs.Bengaluru || [])
                                    .filter(hub => hub.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                                    .map(hub => (
                                      <label key={hub} className="location-option">
                                        <input
                                          type="checkbox"
                                          checked={selectedLocations.inScope.bangalore.includes(hub)}
                                          onChange={() => handleLocationToggle('inScope', 'bangalore', hub)}
                                          onClick={(e) => e.stopPropagation()}
                                          className="location-checkbox"
                                          disabled={loading}
                                        />
                                        <span className="location-label">{hub}</span>
                                      </label>
                                    ))}
                                </div>
                              )}
                            </div>

                            {/* Blank */}
                            <div className="location-option">
                              <input
                                type="checkbox"
                                checked={selectedLocations.inScope.blank}
                                onChange={() => handleLocationToggle('inScope', 'blank')}
                                onClick={(e) => e.stopPropagation()}
                                className="location-checkbox"
                                disabled={loading}
                              />
                              <span className="location-label">Blank</span>
                            </div>
                          </>
                        )}
                    </div>

                      {/* OutScope Section */}
                      <div className="location-category">
                        <div 
                          className="location-category-header"
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocations.outScope.length > 0}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                handleSelectAllOutScope();
                              } else {
                                handleClearAllOutScope();
                              }
                            }}
                            className="location-checkbox"
                            disabled={loading}
                          />
                          <span className="location-category-label">OutScope</span>
                          <span 
                            className="location-expand-icon"
                            onClick={() => toggleCategoryExpansion('outScope')}
                          >
                            {expandedCategories.outScope ? '▼' : '▶'}
                          </span>
                        </div>
                        {expandedCategories.outScope && (
                          <div className="location-substores">
                            {/* Group Pan India hubs separately if available */}
                            {cityHubs['Pan India'] && cityHubs['Pan India'].length > 0 ? (
                              <>
                                <div className="location-substore-group-header">
                                  <span className="location-group-label">Pan India ({cityHubs['Pan India'].length} hubs)</span>
                                </div>
                                {cityHubs['Pan India']
                                  .filter(hub => {
                                    // Always show all Pan India hubs from mapping, regardless of search
                                    if (!locationSearchTerm) return true;
                                    return hub.toLowerCase().includes(locationSearchTerm.toLowerCase());
                                  })
                                  .map(hub => (
                                    <label key={hub} className="location-option">
                                      <input
                                        type="checkbox"
                                        checked={selectedLocations.outScope.includes(hub)}
                                        onChange={() => handleLocationToggle('outScope', null, hub)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="location-checkbox"
                                        disabled={loading}
                                      />
                                      <span className="location-label">{hub}</span>
                                    </label>
                                  ))}
                              </>
                            ) : (
                              // Fallback: Show message if Pan India hubs are not loaded
                              <div className="location-substore-group-header">
                                <span className="location-group-label" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                  Pan India hubs loading...
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={`filter-group ${filters.emailSearch ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!filters.emailSearch}
                onClear={() => clearFilterGroup('emailSearch')}
                loading={loading}
                clearLabel="Clear Email Search filter"
              >
                Email Search
              </FilterGroupHead>
              <input
                type="text"
                value={filters.emailSearch}
                onChange={(e) => handleFilterChange('emailSearch', e.target.value)}
                placeholder="Search by email address"
                className="filter-input"
                disabled={loading}
              />
            </div>

            <div className={`filter-group ${filters.phoneSearch ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!filters.phoneSearch}
                onClear={() => clearFilterGroup('phoneSearch')}
                loading={loading}
                clearLabel="Clear Phone Number Search filter"
              >
                Phone Number Search
              </FilterGroupHead>
              <input
                type="text"
                value={filters.phoneSearch}
                onChange={(e) => handleFilterChange('phoneSearch', e.target.value)}
                placeholder="Search by phone number"
                className="filter-input"
                disabled={loading}
              />
            </div>

            <div className={`filter-group ${filters.appUserType ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!filters.appUserType}
                onClear={() => clearFilterGroup('appUserType')}
                loading={loading}
                clearLabel="Clear App Users filter"
              >
                App Users
              </FilterGroupHead>
              <select
                value={filters.appUserType}
                onChange={(e) => handleFilterChange('appUserType', e.target.value)}
                className="filter-input"
                disabled={loading}
              >
                <option value="">All Users</option>
                <option value="app">App User</option>
                <option value="non-app">Non App User</option>
              </select>
            </div>

            <div className={`filter-group ${filters.customer ? 'applied' : ''}`}>
              <FilterGroupHead
                applied={!!filters.customer}
                onClear={() => clearFilterGroup('customer')}
                loading={loading}
                clearLabel="Clear Customer filter"
              >
                Customer
              </FilterGroupHead>
              <select
                value={filters.customer}
                onChange={(e) => handleFilterChange('customer', e.target.value)}
                className="filter-input"
                disabled={loading}
              >
                <option value="">All</option>
                <option value="yes">Yes (has orders)</option>
                <option value="no">No (no orders)</option>
              </select>
            </div>
          </div>

          <div className="filter-actions">
            {/* Filter Results Count Display */}
            {filteredResults && (
              <div className="filter-results-count">
                <div className="results-info">
                  <span className="results-label">Filtered Results:</span>
                  <span className="results-count">{filteredResults.totalCount.toLocaleString()} customers</span>
                </div>
              </div>
            )}

            <button
              onClick={applyFilters}
              className="apply-filters-btn"
              disabled={applyingFilters || loading}
            >
              {applyingFilters ? (
                <>
                  <div className="spinner"></div>
                  Applying...
                </>
              ) : loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  Loading...
                </>
              ) : (
                <>
                  <Filter size={16} />
                  Apply Filters
                </>
              )}
            </button>

            {filteredResults && (
              <div className="user-group-create">
                <input
                  type="text"
                  className="user-group-name-input"
                  value={userGroupName}
                  onChange={(e) => setUserGroupName(e.target.value)}
                  placeholder="User group name"
                  disabled={creatingUserGroup || loading}
                  aria-label="User group name"
                />
                <button
                  type="button"
                  onClick={createUserGroup}
                  className="create-user-group-btn"
                  disabled={creatingUserGroup || loading || !userGroupName.trim() || !filteredResults.totalCount}
                >
                  {creatingUserGroup ? 'Saving...' : 'Create User Group'}
                </button>
              </div>
            )}
            {creatingUserGroup && userGroupProgressLabel && (
              <div className="user-group-progress">{userGroupProgressLabel}</div>
            )}

            <button
              onClick={handleDownloadWithCampaign}
              className="download-filtered-btn"
              disabled={!filteredResults || loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  Loading...
                </>
              ) : (
                <>
                  <FileDown size={16} />
                  Download Filtered Data
                </>
              )}
            </button>

            <button
              onClick={clearFilters}
              className="clear-filters-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  Loading...
                </>
              ) : (
                'Clear Filters'
              )}
            </button>
          </div>

        </div>
      )}
    </motion.div>
  );
};

export default FiltersContainer;
